// client/src/constants/ntrpDescriptions.js

/**
 * NTRP (National Tennis Rating Program) Rating Descriptions
 *
 * These descriptions help players understand what each NTRP rating level
 * represents so they can choose the level that best matches their ability.
 *
 * Source: USTA National Tennis Rating Program
 * Note: These are general guidelines. Exact wording and criteria can be found
 * in the official USTA NTRP resources.
 */

/**
 * Overview information about NTRP
 */
export const NTRP_OVERVIEW = {
  name: 'NTRP',
  fullName: 'National Tennis Rating Program',
  organization: 'USTA',
  description:
    'The National Tennis Rating Program (NTRP) is the official system of the USTA (United States Tennis Association) for measuring tennis skill levels. It helps ensure fair and competitive play by grouping players of similar abilities.',
}

/**
 * Detailed descriptions for each NTRP rating level
 *
 * NOTE: This includes the full NTRP range (1.0-7.0) for reference and
 * potential future expansion. The current league registration only uses
 * levels 2.5-6.0 (defined in client/src/constants/skillLevels.js).
 *
 * Additional levels are preserved here for:
 * - Documentation of the complete NTRP system
 * - Future expansion if lower/higher skill levels are added
 * - Consistency with official USTA NTRP documentation
 */
export const NTRP_LEVEL_DESCRIPTIONS = Object.freeze({
  1.0: {
    title: 'Beginner',
    description:
      'Just starting to play tennis; learning how to hit the ball into play and getting comfortable with basic movement and rules.',
  },
  1.5: {
    title: 'Beginner',
    description:
      'Has limited experience, still focused primarily on getting the ball in play more consistently.',
  },
  2.0: {
    title: 'Early Beginner',
    description:
      'Needs on-court experience; familiar with playing positions and basic strokes but has obvious weaknesses.',
  },
  2.5: {
    title: 'Beginner with Short Rallies',
    description:
      'Learning to judge where the ball is going; able to sustain brief, slow rallies but court coverage and consistency are limited.',
  },
  3.0: {
    title: 'Intermediate',
    description:
      'Fairly consistent with medium-paced shots; may lack comfort with all strokes and execution on depth, power, or direction.',
  },
  3.5: {
    title: 'Improving Intermediate',
    description:
      'Improved stroke dependability, better court coverage, and more aggressive net play; still developing depth and variety.',
  },
  4.0: {
    title: 'Advanced Intermediate',
    description:
      'Dependable strokes with directional control and depth; can handle lobs, overheads, approach shots, and volleys; tactically aware.',
  },
  4.5: {
    title: 'Advanced',
    description:
      'Uses pace and spin effectively with strong footwork; consistent serving and strategy; can execute advanced shots under pressure.',
  },
  5.0: {
    title: 'Expert',
    description:
      'Outstanding power and consistency; able to dictate play, force errors, anticipate well, and execute advanced strategies.',
  },
  5.5: {
    title: 'Highly Skilled Competitor',
    description:
      'Developed pace, consistency, and strategy; dependable under stress and can vary style of play.',
  },
  6.0: {
    title: 'National/Elite Level',
    description:
      'Typically has intensive training and competition experience at high levels (national or top collegiate).',
  },
  6.5: {
    title: 'World Class',
    description:
      'World-class player with extensive tournament experience and potential to compete at the highest level.',
  },
  7.0: {
    title: 'Touring Pro',
    description:
      'Top professional ability, competing at the highest levels of the sport.',
  },
})

/**
 * Get NTRP description for a specific level
 * @param {number} level - The NTRP rating level
 * @returns {object|null} Description object or null if not found
 */
export function getNTRPDescription(level) {
  return NTRP_LEVEL_DESCRIPTIONS[level] || null
}

/**
 * Check if a level has a description available
 * @param {number} level - The NTRP rating level
 * @returns {boolean} True if description exists
 */
export function hasNTRPDescription(level) {
  return level in NTRP_LEVEL_DESCRIPTIONS
}
